import React, { useEffect, useRef, useState } from 'react'
import type { PlaybackCore } from './core/PlaybackCore'

// ?qualityinfo=1 — a measuring panel for one question (Gavin, 3 Nov: "quality
// on mobiles are set low"). He opens a link on his iPhone and his Android,
// plays for a minute, and copies the panel back to us. It shows which rung of
// the ladder is actually being decoded, and the things that decide it:
//
//   iPhone   Safari's own HLS player picks the rung, partly from how big the
//            <video> element is on the page. In the sphere that element is
//            hidden, sized to the player box — small on a phone.
//   Android  our player (VHS) picks from measured bandwidth alone.
//
// The iPhone experiment that rode on the same link, ?bigvideo=1, is now the
// default on iPhone and iPad (bigVideoHost below); ?bigvideo=0 turns it off.
// On Android, ?debug=true adds the full ladder to Settings so a rung can
// be pinned by hand, to see whether the phone can hold it at all.
//
// Nothing renders, and nothing polls, without the parameter. Captured at
// module load for the reason designPreview.ts gives: the public page's
// redirect to the canonical slug drops the query string before the player
// mounts.
const initialSearch = typeof window === 'undefined' ? '' : window.location.search
const params = new URLSearchParams(initialSearch)
const on = (v: string | null) => v === '1' || v === 'true'
export const qualityReadoutEnabled = on(params.get('qualityinfo'))

// The hidden video element at the top rung's own size (3840×1920) instead of
// the player box, on iPhone and iPad. Safari's own HLS player will not pick a
// rung much bigger than the element it plays into, and in the sphere that
// element is hidden, so its size is ours to choose. Gavin's iPhone SE, 3 Nov:
// box-sized (667×311) it settled on 720p; at 3840×1920 it went straight to
// 1440p and held it for half an hour, 0 frames dropped. The size is a ceiling,
// not a floor: Safari still steps down on a slow connection.
//
// Not desktop Safari: its player box is already big, and the top rung is
// ~38 Mbps. ?bigvideo=1 forces it on anywhere, ?bigvideo=0 off, to compare.
const isIOSDevice = typeof navigator !== 'undefined' && (
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
)
// ?rendition=1440p pins one rung of the ladder (by the name the debug menu
// shows: 4K, 1440p, 1080p, 720p…) — the test the ?debug=true menu offers,
// for a phone held sideways, whose menu is too short to reach the list.
export const pinnedRendition = params.get('rendition')?.toLowerCase() ?? null

const bigVideoParam = params.get('bigvideo')
export const bigVideoHost = bigVideoParam === null ? isIOSDevice : on(bigVideoParam)

const device = (ua: string): string => {
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad'
  if (/Android/.test(ua)) return 'Android'
  return 'desktop'
}

const mbps = (bits: unknown) => (typeof bits === 'number' && Number.isFinite(bits) ? `${(bits / 1e6).toFixed(1)} Mbps` : '—')

// The ladder's rungs by width, named as Bunny names its folders.
const RUNG_NAMES: Record<number, string> = {
  3840: '2160p (top)', 2560: '1440p', 1920: '1080p', 1280: '720p', 856: '480p', 640: '360p', 428: '240p (bottom)',
}
const rungName = (w: number, h: number) => (w ? `${w}×${h}${RUNG_NAMES[w] ? ` = ${RUNG_NAMES[w]}` : ''}` : 'nothing decoded yet')

interface VhsLike {
  bandwidth?: number
  systemBandwidth?: number
  throughput?: number
  playlists?: { media?: () => { attributes?: { RESOLUTION?: { width: number; height: number }; BANDWIDTH?: number } } | undefined }
}

const readVhs = (core: PlaybackCore): VhsLike | null => {
  try {
    const tech = (core.player as unknown as { tech?: (o: { IWillNotUseThisInPlugins: boolean }) => unknown })
      .tech?.({ IWillNotUseThisInPlugins: true })
    return (tech as { vhs?: VhsLike })?.vhs ?? null
  } catch {
    return null
  }
}

interface QualityReadoutProps {
  core: PlaybackCore | null
  surface: string
}

const QualityReadout: React.FC<QualityReadoutProps> = ({ core, surface }) => {
  const [, tick] = useState(0)
  const [copied, setCopied] = useState(false)
  // Every rung change, with the playback time it happened at.
  const history = useRef<string[]>([])
  const last = useRef('')
  const started = useRef(Date.now())

  useEffect(() => {
    const id = window.setInterval(() => {
      const el = core?.videoElement
      if (el && el.videoWidth) {
        const now = `${el.videoWidth}×${el.videoHeight}`
        if (now !== last.current) {
          last.current = now
          const secs = Math.round((Date.now() - started.current) / 1000)
          history.current = [...history.current, `${secs}s ${now}`].slice(-8)
        }
      }
      tick((n) => n + 1)
    }, 1000)
    return () => window.clearInterval(id)
  }, [core])

  const el = core?.videoElement ?? null
  const vhs = core ? readVhs(core) : null
  const media = vhs?.playlists?.media?.()
  const box = el?.getBoundingClientRect()
  const pq = el?.getVideoPlaybackQuality?.()
  let ahead = 0
  try {
    if (el && el.buffered.length) ahead = el.buffered.end(el.buffered.length - 1) - el.currentTime
  } catch {
    // a torn-down element throws
  }
  const ua = navigator.userAgent
  // What Chrome itself guesses (navigator.connection), which VHS used to trust over its own measurement.
  const downlink = (navigator as unknown as { connection?: { downlink?: number } }).connection?.downlink
  const chromeDownlink = typeof downlink === 'number' ? `, Chrome guesses ${downlink} Mbps` : ''
  const experiments = bigVideoHost ? 'bigvideo' : 'none'

  const rows: [string, string][] = [
    ['device', `${device(ua)}, screen ${window.screen.width}×${window.screen.height} @${window.devicePixelRatio}x`],
    ['engine', vhs ? 'our player (VHS)' : "Safari's own HLS"],
    ['surface', surface],
    ['playing', el ? rungName(el.videoWidth, el.videoHeight) : '—'],
    ['history', history.current.join(' → ') || '—'],
    ['asked', media?.attributes?.RESOLUTION ? `${media.attributes.RESOLUTION.width}×${media.attributes.RESOLUTION.height} (${mbps(media.attributes.BANDWIDTH)})` : '—'],
    ['speed', vhs ? `estimate ${mbps(vhs.bandwidth)}, measured ${mbps(vhs.throughput)}${chromeDownlink}` : 'not visible on Safari'],
    ['element', box ? `${Math.round(box.width)}×${Math.round(box.height)} css px` : '—'],
    ['frames', pq ? `${pq.droppedVideoFrames} dropped of ${pq.totalVideoFrames}` : '—'],
    ['buffer', `${ahead.toFixed(1)}s ahead, ${el?.paused ? 'paused' : 'playing'} at ${el ? Math.round(el.currentTime) : 0}s`],
    ['tests', experiments],
  ]
  const text = [...rows.map(([k, v]) => `${k}: ${v}`), `ua: ${ua}`].join('\n')

  const copy = () => {
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500) }
    navigator.clipboard?.writeText(text).then(done, () => window.prompt('Copy this:', text))
      ?? window.prompt('Copy this:', text)
  }

  return (
    <div
      data-player-controls
      style={{
        position: 'absolute',
        top: 44,
        left: 12,
        zIndex: 11,
        maxWidth: 'calc(100% - 24px)',
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.78)',
        color: '#fff',
        font: '11px/1.4 ui-monospace, Menlo, Consolas, monospace',
        borderRadius: 4,
        pointerEvents: 'auto',
        userSelect: 'text',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontWeight: 700, marginBottom: 4 }}>
        <span>quality readout</span>
        <button
          onClick={copy}
          style={{ font: 'inherit', color: '#000', background: '#fff', border: 0, borderRadius: 3, padding: '1px 8px', cursor: 'pointer' }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 8 }}>
          <span style={{ opacity: 0.6, minWidth: 50, flexShrink: 0 }}>{k}</span>
          <span style={{ wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  )
}

export default QualityReadout
